<script setup>
import { Head,  Link } from '@inertiajs/inertia-vue3';
import {ref, onMounted, reactive} from "vue";
import Navlink from '../Components/NavLink.vue'
import Dropdown from '../utils/Dropdown.vue'

const props = defineProps({
  brandInfo: {
    type: Object,
    default: null
  },
})

const logoImg = ref(null)
const logoLoadFailed = ref(false)
const alternativePaths = [
  '/images/logo/business_compliance_services_logo.png',
  '/images/logo.png',
]
let currentPathIndex = 0

// Handle image loading errors
const handleImageError = () => {
  // Try next alternative path if available
  if (currentPathIndex < alternativePaths.length) {
    if (logoImg.value) {
      logoImg.value.src = alternativePaths[currentPathIndex]
      currentPathIndex++
    }
  } else {
    // If all alternatives fail, show text instead
    logoLoadFailed.value = true
  }
}

</script>
<template>
  <footer v-if="brandInfo?.footer" class="w-full bg-slate-900 mt-10">
    <div class="flex flex-col gap-8 max-w-6xl mx-auto px-4 sm:px-6 lg:px-4 py-12 md:py-16">

      <!-- Top area: Logo and Navigation -->
      <div class="flex flex-col justify-between gap-10 lg:flex-row">

        <!-- Logo and slogan block -->
        <div class="flex flex-col gap-4 items-start max-w-[300px]">
          <div class="inline-flex">
            <img
              v-if="brandInfo.footer.image"
              :src="brandInfo.footer.image"
              style="height: 48px; width: auto;"
              @error="handleImageError"
              ref="logoImg"
            >
            <span v-if="!brandInfo.footer.image || logoLoadFailed" class="text-lg font-bold text-white">
              {{ brandInfo.header?.title || 'Business Compliance Services' }}
            </span>
          </div>
          <h5 class="text-lg font-medium text-slate-400">{{brandInfo.footer.slogan}}</h5>

          <!-- Social links -->
          <div class="flex flex-row gap-4 mt-2">
            <a href="#" title="Facebook" class="text-slate-400 hover:text-[rgb(var(--brand-accent-400))] transition duration-200">
              <svg class="w-5 h-5 fill-current" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 10.025C20 4.491 15.52 0 10 0S0 4.491 0 10.025c0 4.852 3.44 8.892 8 9.825v-6.817H6v-3.008h2V7.52a3.508 3.508 0 0 1 3.5-3.509H14v3.008h-2c-.55 0-1 .45-1 1.002v2.005h3v3.008h-3V20c5.05-.501 9-4.772 9-9.975Z" />
              </svg>
            </a>
            <a href="#" title="Twitter" class="text-slate-400 hover:text-[rgb(var(--brand-accent-400))] transition duration-200">
              <svg class="w-5 h-5 fill-current" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 3.897c-.75.33-1.5.577-2.333.66A4.4 4.4 0 0 0 19.5 2.33c-.833.495-1.667.825-2.583.99a4.053 4.053 0 0 0-3-1.32c-2.25 0-4.084 1.814-4.084 4.041 0 .33 0 .66.084.907-3.5-.164-6.5-1.814-8.5-4.288C1 3.32.833 3.98.833 4.722c0 1.402.75 2.639 1.834 3.381-.667 0-1.334-.165-1.834-.495v.083c0 1.98 1.417 3.629 3.25 3.958-.333.083-.666.165-1.083.165-.25 0-.5 0-.75-.082.5 1.65 2 2.804 3.833 2.804C4.667 15.608 2.917 16.268 1 16.268c-.333 0-.667 0-1-.082C1.833 17.34 4 18 6.25 18c7.583 0 11.667-6.186 11.667-11.546v-.495c.833-.578 1.5-1.32 2.083-2.062Z" />
              </svg>
            </a>
            <a href="#" title="Github" class="text-slate-400 hover:text-[rgb(var(--brand-accent-400))] transition duration-200">
              <svg class="w-5 h-5 fill-current" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.041 0C4.52 0 0 4.612 0 10.25c0 4.526 2.845 8.37 6.862 9.737.502.085.669-.257.669-.513v-1.708c-2.761.598-3.347-1.367-3.347-1.367-.419-1.196-1.088-1.537-1.088-1.537-1.004-.598 0-.598 0-.598 1.004.085 1.506 1.025 1.506 1.025.92 1.537 2.343 1.11 2.929.854.084-.683.335-1.11.67-1.367-2.26-.256-4.603-1.11-4.603-5.039 0-1.11.419-2.05 1.004-2.733 0-.342-.418-1.367.168-2.733 0 0 .836-.257 2.76 1.025.838-.257 1.674-.342 2.511-.342.837 0 1.674.085 2.51.342 1.925-1.367 2.762-1.025 2.762-1.025.586 1.452.167 2.477.084 2.733.669.683 1.004 1.623 1.004 2.733 0 3.93-2.343 4.783-4.603 5.04.335.341.67.939.67 1.879v2.818c0 .256.167.598.67.513 4.016-1.367 6.86-5.21 6.86-9.737C20.084 4.612 15.565 0 10.042 0Z" />
              </svg>
            </a>
            <a href="#" title="Telegram" class="text-slate-400 hover:text-[rgb(var(--brand-accent-400))] transition duration-200">
              <svg class="w-5 h-5 fill-current" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.96 2.336a.421.421 0 0 0-.291-.308 1.543 1.543 0 0 0-.788.054S1.358 8.194.358 8.87c-.215.145-.288.23-.324.33-.173.485.366.694.366.694l4.517 1.428a.506.506 0 0 0 .229-.013c1.026-.63 10.332-6.335 10.873-6.527.083-.024.148 0 .131.061-.215.732-8.257 7.664-8.301 7.706a.16.16 0 0 0-.06.143l-.422 4.28s-.176 1.331 1.196 0a38.082 38.082 0 0 1 2.374-2.11c1.553 1.041 3.224 2.192 3.945 2.794.245.23.576.354.916.342.426-.05.774-.35.876-.754 0 0 3.192-12.471 3.298-14.142.011-.162.025-.268.027-.38.005-.13-.008-.26-.04-.387Z" />
              </svg>
            </a>
          </div>
        </div>

        <!-- Navigation blocks -->
        <div class="flex flex-wrap items-start gap-8 lg:gap-12">
          <div v-for="section in brandInfo.footer.navigation" :key="section.title" class="flex flex-col gap-3 min-w-[140px]">
            <h6 class="text-sm font-semibold text-white uppercase tracking-wider mb-2">{{section.title}}</h6>
            <ul class="flex flex-col gap-2">
              <li v-for="link in section.links" :key="link.title">
                <a class="text-slate-400 hover:text-[rgb(var(--brand-accent-400))] transition duration-200"
                   :href="link.url"
                >{{ link.title }}</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Divider -->
      <hr class="border-slate-700">

      <!-- Important Notice Disclaimer -->
      <div class="bg-slate-800 border border-slate-700 rounded-lg p-4">
        <p class="text-xs text-slate-400 leading-relaxed">
          <span class="font-semibold text-slate-300">IMPORTANT NOTICE:</span>
          You are NOT required to purchase anything from this company and the company is NOT affiliated, endorsed, or approved by any governmental entity. The item offered in this advertisement has NOT been approved or endorsed by any governmental agency, and this offer is NOT being made by an agency of the government.
        </p>
      </div>

      <!-- Bottom area: Copyright -->
      <div class="flex flex-col-reverse justify-between gap-4 sm:flex-row items-center">
        <p class="text-sm text-slate-500">&copy; {{ brandInfo.footer.copyright }} 2024-{{ new Date().toJSON().slice(0,4).replace(/-/g,'/') }} All rights reserved.</p>
      </div>

    </div>
  </footer>
</template>
